import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Send, Sparkles, Info } from "lucide-react";
import { qaApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function FreeQuery() {
  const { projectId } = useParams();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    qaApi.list(projectId).then(setList);
  }, [projectId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [list]);

  const send = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await qaApi.ask(projectId, q);
      setList((l) => [r, ...l]);
      setQ("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const suggestions = [
    "Quelles conditionnalités ont le plus d'impact sur la santé ?",
    "Quel est le coût vitaliste total des emprunts non-concessionnels ?",
    "Comparer les taux des conventions extraites.",
    "Générer un résumé exécutif pour un parlementaire non-technicien.",
    "Quelles clauses limitent la capacité d'emprunt futur ?",
    "Quel est l'impact projeté si la dette augmente de 20% ?",
  ];

  return (
    <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto w-full" data-testid="qa-page">
      <div className="label-mono text-[10px] text-muted-foreground">MODULE 2.6</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Requête libre — Q&A</h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Posez toute question sur les documents chargés dans ce projet. Réponses citant les sources.
      </p>

      <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground border border-border rounded-sm p-3 bg-muted/20">
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>
          Les réponses sont générées par IA et doivent être vérifiées avec les sources originales.
          Avertissement : les analyses juridiques n'ont pas de valeur juridictionnelle.
        </span>
      </div>

      <form onSubmit={send} className="mt-6" data-testid="qa-form">
        <Textarea value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Ex. Quel est le service total de la dette sur la période 2020-2023, et son coût en jours de vie ?"
                  className="min-h-[100px]" data-testid="qa-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} />
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Ctrl/Cmd + Enter pour envoyer</span>
          <Button type="submit" disabled={loading || !q.trim()} className="bg-marine hover:bg-marine/90 text-white"
                  data-testid="qa-send-btn">
            <Send size={14} className="mr-2" /> {loading ? "Réflexion…" : "Envoyer"}
          </Button>
        </div>
      </form>

      {list.length === 0 && (
        <div className="mt-8 border border-dashed border-border rounded-sm p-6">
          <div className="label-mono text-[10px] text-gold">SUGGESTIONS</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s} onClick={() => setQ(s)}
                      className="border border-border text-left px-3 py-2 text-xs rounded-sm hover:bg-accent/30 hover:border-gold transition">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 space-y-5" data-testid="qa-history">
        {list.map((item) => (
          <div key={item.id} className="border border-border bg-card rounded-sm p-5">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-sm bg-marine text-white grid place-items-center shrink-0">
                <span className="text-xs font-bold">Q</span>
              </div>
              <div className="flex-1">
                <p className="font-serif font-bold">{item.question}</p>
                <div className="text-[10px] label-mono text-muted-foreground mt-1">
                  {new Date(item.created_at).toLocaleString("fr-FR")}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-3">
              <div className="h-8 w-8 rounded-sm bg-gold text-marine grid place-items-center shrink-0">
                <Sparkles size={14} />
              </div>
              <div className="flex-1">
                <div className="label-mono text-[9px] text-gold">IA · Vérifier avec sources</div>
                <div className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{item.answer}</div>
                {item.sources?.length > 0 && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    Sources : {item.sources.slice(0, 6).map((s) => s.filename).join(" · ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div ref={bottomRef} />
    </div>
  );
}
